/**
 * DLP の端末 `/dlp/devices`。エージェントの poll と、デバイス管理の `device.assigned` イベントから集まる
 * (mdm の台帳は直接見ない。docs/dlp.md §2)。行を押すと `?panel=<id>` で右に詳細。USB モードの変更は admin。
 */
import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { DlpDevice } from "../../lib/api";
import {
  DataTable,
  FilterBar,
  PageHeader,
  TimeAgo,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { employeeCell, uploadStateTag, usbModeTag } from "./bits";
import { DevicePanel } from "./DevicePanel";
import { errorText, useDlpDevices } from "./hooks";

export function DevicesPage() {
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const query = useDlpDevices({ ...(q ? { q } : {}), limit: String(pageSize), offset: String((page - 1) * pageSize) });
  useEffect(() => setPage(1), [q]);

  const columns: DataColumn<DlpDevice>[] = [
    { key: "serial", title: "製造番号", ellipsis: false, dataIndex: "serial", width: 150, render: (v: string) => <span className="ui-num">{v}</span> },
    { key: "hostname", title: "ホスト名", ellipsis: false, dataIndex: "hostname", width: 160 },
    { key: "os", title: "OS", ellipsis: false, dataIndex: "os", width: 160 },
    { key: "employee", title: "従業員", ellipsis: false, width: 200, render: (_: unknown, r) => employeeCell(r.employee) },
    { key: "agent", title: "エージェント", ellipsis: false, dataIndex: "agent_version", width: 160, render: (v: string) => <span className="ui-num">{v}</span> },
    { key: "last_poll", title: "最終 poll", ellipsis: false, dataIndex: "last_poll", width: 160, render: (v: string | null) => (v ? <TimeAgo iso={v} /> : "") },
    { key: "usb_mode", title: "USB", ellipsis: false, width: 120, render: (_: unknown, r) => usbModeTag(r.usb_mode) },
    { key: "upload", title: "アップロード観測", ellipsis: false, width: 144, render: (_: unknown, r) => uploadStateTag(r.upload_status?.state) },
  ];
  const visibleColumns = panelId ? columns.filter((c) => ["serial", "hostname", "employee"].includes(String(c.key))) : columns;
  const filtered = Boolean(q);

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="端末"
          description={
            <>
              エージェントが poll してきた端末。従業員は<Link to="/mdm/devices">デバイス台帳</Link>の割当イベントから写す
              (製造番号で突き合わせる)。
            </>
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "製造番号 / ホスト名 / 従業員" }}
          right={
            filtered ? (
              <Button
                size="small"
                onClick={() => {
                  setQInput("");
                  patch({ q: null }, true);
                }}
              >
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="端末を取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<DlpDevice>
          columns={visibleColumns}
          scrollX={visibleColumns.reduce((sum, c) => sum + c.width, 0)}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? 0}
          unit="台"
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered ? "条件に合う端末はない" : "端末はまだない。エージェントが /api/dlp/agent/poll を叩くと現れる",
            action: filtered ? (
              <Button
                onClick={() => {
                  setQInput("");
                  patch({ q: null }, true);
                }}
              >
                条件を外す
              </Button>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <DevicePanel id={Number(panelId)} onClose={() => setPanelId(null)} />}
    </div>
  );
}
